/**
 * Shared evidence form used by both create and edit views.
 *
 * Uses Refine's react-hook-form `useForm` with a Zod resolver bound to the
 * shared `createEvidenceSchema`. Empty optional strings and non-numeric number
 * fields are normalised before submission so the worker receives clean JSON.
 */
import { useForm } from "@refinedev/react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigation, type BaseRecord, type HttpError } from "@refinedev/core";
import {
  CLAIM_TYPES,
  EVIDENCE_POLARITY,
  createEvidenceSchema,
  type CreateEvidenceInput,
} from "@aos/shared";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { SelectField, TextAreaField, TextField } from "../_shared/FormFields";

type FormValues = CreateEvidenceInput;

/** Drop empty-string and NaN fields so optional columns stay null/absent. */
function clean(values: FormValues): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) {
    if (value === "" || value === undefined) continue;
    if (typeof value === "number" && Number.isNaN(value)) continue;
    out[key] = value;
  }
  return out;
}

export function EvidenceForm({ action }: { action: "create" | "edit" }) {
  const { list } = useNavigation();
  const {
    refineCore: { onFinish, formLoading },
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<BaseRecord, HttpError, FormValues>({
    resolver: zodResolver(createEvidenceSchema),
    refineCoreProps: { resource: "evidence", action, redirect: "show" },
  });

  const submit = handleSubmit((values) => {
    void onFinish(clean(values) as FormValues);
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {action === "create" ? "New evidence" : "Edit evidence"}
        </h1>
      </div>
      <form onSubmit={submit}>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Evidence</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <TextField<FormValues>
              name="title"
              label="Title"
              required
              register={register}
              error={errors.title}
            />
            <TextField<FormValues>
              name="evidence_type"
              label="Evidence type"
              required
              register={register}
              error={errors.evidence_type}
              placeholder="e.g. observation, citation, measurement"
            />
            <TextAreaField<FormValues>
              name="body"
              label="Body (markdown)"
              register={register}
              error={errors.body}
              rows={6}
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextField<FormValues>
                name="confidence_score"
                label="Confidence score (0–1)"
                type="number"
                register={register}
                error={errors.confidence_score}
              />
              <TextField<FormValues>
                name="weight"
                label="Weight"
                type="number"
                register={register}
                error={errors.weight}
              />
            </div>
            <TextField<FormValues>
              name="artifact_id"
              label="Artifact id"
              register={register}
              error={errors.artifact_id}
              placeholder="art_…"
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <SelectField<FormValues>
                name="polarity"
                label="Polarity"
                register={register}
                error={errors.polarity}
                options={EVIDENCE_POLARITY}
                includeEmpty={false}
              />
              <SelectField<FormValues>
                name="claim_type"
                label="Claim type"
                register={register}
                error={errors.claim_type}
                options={CLAIM_TYPES}
              />
            </div>
            <TextField<FormValues>
              name="claim_id"
              label="Claim id"
              register={register}
              error={errors.claim_id}
              placeholder="clm_… (target this evidence supports/contradicts)"
            />
            <TextAreaField<FormValues>
              name="interpretation"
              label="Interpretation"
              register={register}
              error={errors.interpretation}
              rows={3}
              placeholder="How this evidence bears on the claim."
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextField<FormValues>
                name="source_event_id"
                label="Source event id"
                register={register}
                error={errors.source_event_id}
              />
              <TextField<FormValues>
                name="source_document_id"
                label="Source document id"
                register={register}
                error={errors.source_document_id}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextField<FormValues>
                name="source_signal_id"
                label="Source signal id"
                register={register}
                error={errors.source_signal_id}
                placeholder="sig_… (set when created from a signal)"
              />
              <TextField<FormValues>
                name="source_artifact_id"
                label="Source artifact id"
                register={register}
                error={errors.source_artifact_id}
                placeholder="art_…"
              />
            </div>
          </CardContent>
        </Card>
        <div className="mt-4 flex gap-2">
          <Button type="submit" disabled={formLoading}>
            {formLoading ? "Saving…" : "Save"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => list("evidence")}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

export default EvidenceForm;
